import React, { useCallback, useEffect, useState } from 'react';
import { EllipsisVIcon } from '@patternfly/react-icons';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon, BanIcon, CheckIcon } from '@patternfly/react-icons';
import { StackItem, Modal, ModalVariant, Button, Split, SplitItem, Tooltip, DropdownItem, Dropdown, MenuToggle, MenuToggleElement, FormGroup, } from '@patternfly/react-core';

import { ModelSelectionUndoState } from './ModelSelectionPage';
import { getFormValidationErrorMessage } from '../../store/global-types/form-errors';
import { defaultValidation, FieldValidationState, validationStatusToPatternFly } from '../../components/validation-types';
import { DicomAttributeRule, ModelSelectionScope, ModelType } from '../../store/global-types/customization-types';
import { FormValidationError, getFormErrorsWorstSeverity } from '../../store/global-types/store-errors';
import { RetainedDicomAttributes, RetainStatus } from '../../store/global-types/daemon-types';
import RegexField from '../../components/regex-field';
import TextInputWithPresets, { TextInputWithPresetsValidation } from '../../components/text-input-with-presets';
import ValidationHelperText from '../../components/validation-helper-text';



interface DicomAttributeRuleFormProps {
    modelType: ModelType,
    dicomAttributeRule: DicomAttributeRule,
    formValidationErrors: FormValidationError[],
    runningNumber: number,
    setUndoState: (undoState: ModelSelectionUndoState) => void,
    isUndoDisabled?: boolean,
    retainedDicomAttributes: RetainedDicomAttributes,
    onRemoveDicomRule: () => void,
    onDicomAttributeRuleRemoved: () => void,
    onDicomAttributeRuleUpdated: (attribute: string, value: string) => void,
}

const DicomAttributeRuleForm = (props: DicomAttributeRuleFormProps) => {
    const { dicomAttributeRule, formValidationErrors, runningNumber, setUndoState, isUndoDisabled,
        retainedDicomAttributes, onRemoveDicomRule, onDicomAttributeRuleRemoved, onDicomAttributeRuleUpdated } = props;

    const { t } = useTranslation();

    const [isActionListOpen, setActionListOpenState] = useState(false);
    const [isRemoveAttributeModalOpen, setRemoveAttributeModalOpenState] = useState(false);
    // const [isAttributeHovered, setAttributeHovered] = useState(false);
    const [nameFieldValidation, setNameFieldValidation] = useState<FieldValidationState>(defaultValidation);

    /** Set non-blocking form validation errors generated in redux store reducers to be visible in this form's normal UI validation. */
    useEffect(() => {
        if (formValidationErrors.length > 0) {
            const allErrorsAsMessage = formValidationErrors.map(e => getFormValidationErrorMessage(e.errorType)).join(' ');
            setNameFieldValidation({ validated: validationStatusToPatternFly(getFormErrorsWorstSeverity(formValidationErrors)), helperTextInvalid: allErrorsAsMessage });
        } else {
            setNameFieldValidation(defaultValidation);
        }
    }, [formValidationErrors, dicomAttributeRule]);

    const handleRemoveAttributeButtonClick = useCallback(() => {
        setActionListOpenState(false);
        setRemoveAttributeModalOpenState(true);
    }, []);


    const handleRemoveAttributeConfirmation = useCallback(() => {
        setRemoveAttributeModalOpenState(false);
        onDicomAttributeRuleRemoved();
    }, [onDicomAttributeRuleRemoved]);


    const handleDicomAttributeNameChange = useCallback((name: string) => {
        onDicomAttributeRuleUpdated(name, dicomAttributeRule.value);
    }, [dicomAttributeRule, onDicomAttributeRuleUpdated]);

    const handleDicomAttributeValueChange = useCallback((regexValue: string) => {
        onDicomAttributeRuleUpdated(dicomAttributeRule.attribute, regexValue);
    }, [dicomAttributeRule.attribute, onDicomAttributeRuleUpdated]);

    const handleUndo = useCallback(() => {
        setUndoState({ isModalOpen: true, scope: ModelSelectionScope.DicomAttributeRule, id: dicomAttributeRule.id });
        setActionListOpenState(false);
    }, [dicomAttributeRule, setUndoState]);

    const getRetainedAttributeIcon = ((attribute: string): React.ReactNode | null => {

        let isRetainedInAllModels = false;
        let isRetainedInSomeModels = false;

        // check if the attribute name is present in the RetainedDicomAttributes object
        if (retainedDicomAttributes[attribute] !== undefined) {
            if (retainedDicomAttributes[attribute] === RetainStatus.Retained) {
                isRetainedInAllModels = true;
            } else if (retainedDicomAttributes[attribute] === RetainStatus.RetainedInSome) {
                isRetainedInSomeModels = true;
            }
        }

        if (isRetainedInAllModels) {
            return <Tooltip content={t("selectionPage.dicomAttributePresets.retainedInAll")}><CheckIcon className='dicom-attributes-helper-icons' /></Tooltip>;
        } else if (isRetainedInSomeModels) {
            return <Tooltip content={t("selectionPage.dicomAttributePresets.retainedInSome")}><ExclamationTriangleIcon className='dicom-attributes-helper-icons' /></Tooltip>;
        } else {
            return <Tooltip content={t("selectionPage.dicomAttributePresets.NotRetained")}><BanIcon className='dicom-attributes-helper-icons' /></Tooltip>;
        }
    });

    // don't render anything if we don't have a valid object
    if (dicomAttributeRule === undefined) {
        return null;
    }

    // TODO: this can be used to display which action menu item (attribute or entire rule) is being targeted by an action but
    // it will require some extra ui work to clean the hover style in weird cases (like pressing esc when the action menu is
    // open, or if a modal is opened). Similar approach can be used to style the entire rule.
    const hoveredClass = '';// isAttributeHovered ? 'attribute-hovered' : '';

    // any non-default validation state surfaces the helper text. `TextInputWithPresetsValidation`
    // currently only has Error/Default — Warning maps to Error so the user sees the attention cue
    // (rather than silently hiding it) until/unless the underlying enum gains a Warning state.
    const formValidationState = nameFieldValidation.validated !== 'default' ? TextInputWithPresetsValidation.Error : TextInputWithPresetsValidation.Default;

    const dropdownItems = [
        <DropdownItem key="removeAttribute" /*onMouseEnter={handleRemoveAttributeHover} onMouseLeave={handleRemoveAttributeHoverLeave}*/ onClick={handleRemoveAttributeButtonClick}>{t('selectionPage.removeDicomAttributeRule')}</DropdownItem>,
        <DropdownItem key="removeDicomRule" onClick={onRemoveDicomRule}>{t('selectionPage.removeDicomRule')}</DropdownItem>
    ];
    if (!isUndoDisabled) {
        dropdownItems.unshift(<DropdownItem key="undoDicomAttributeRuleChanges" isDisabled={!dicomAttributeRule.isModified || dicomAttributeRule.isNew}
            onClick={handleUndo}>{t('selectionPage.undoDicomAttributeRuleChanges')}</DropdownItem>);
    }

    return (
        <>
            <StackItem className={`rule-item dicom-rule-item ${hoveredClass}`}>
                <Split className="dicom-attribute-row" hasGutter>
                    <SplitItem className="dicom-attribute-column">
                        <FormGroup
                            label={t('selectionPage.dicomAttributeRuleNumber', { number: runningNumber })}
                            fieldId={`attribute-name-field-for-${dicomAttributeRule.id}`}
                            title={t('selectionPage.dicomAttributePresets.tooltip.field')}
                        >
                            <TextInputWithPresets
                                fieldId={`attribute-name-field-for-${dicomAttributeRule.id}`}
                                presetsTitle={t('selectionPage.dicomAttributePresetsLabel')}
                                tooltip={t('selectionPage.dicomAttributePresets.tooltip.presets')}
                                onTextChange={handleDicomAttributeNameChange}
                                defaultValue={dicomAttributeRule.attribute}
                                validated={formValidationState}
                                placeholder={t('selectionPage.dicomAttributePresetsPlaceholder')}
                                showFreeTextEntryPreset
                                getPresetIcon={getRetainedAttributeIcon}
                                presets={[
                                    'PatientSex',
                                    'ProtocolName',
                                    'SeriesDescription',
                                    'BodyPartExamined',
                                    'ImageComments',
                                    'Laterality',
                                    'ScanOptions',
                                    'StationName',
                                    'StudyDescription',
                                ]}
                                testId="dicom-rule-attribute-input"
                            />
                            {formValidationState !== TextInputWithPresetsValidation.Default && (
                                <ValidationHelperText
                                    validated={formValidationState}
                                    helperText={nameFieldValidation.helperTextInvalid}
                                />
                            )}
                        </FormGroup>
                    </SplitItem>
                    <SplitItem isFilled>
                        <RegexField
                            id={dicomAttributeRule.id}
                            label={t('selectionPage.requiredValue')}
                            regexModeLabel={t('selectionPage.requiredValue.regex')}
                            onChange={handleDicomAttributeValueChange}
                            regexValue={dicomAttributeRule.value}
                            title={t('regex.valueRange')}
                            enableComposer
                            testId="dicom-rule-value-input"
                        />
                    </SplitItem>
                    <SplitItem>
                        <Dropdown
                            toggle={(toggleRef: React.Ref<MenuToggleElement>) => <MenuToggle ref={toggleRef} onClick={() => setActionListOpenState(!isActionListOpen)} variant='plain'>
                                <EllipsisVIcon />
                            </MenuToggle>}
                            isPlain
                            shouldFocusFirstItemOnOpen={false}
                            popperProps={{ position: 'right' }}
                            isOpen={isActionListOpen}
                            onOpenChange={() => setActionListOpenState(!isActionListOpen)}
                        >
                            {dropdownItems}</Dropdown>
                    </SplitItem>
                </Split>
                {/* <Divider className="dicom-rule-divider" /> */}
            </StackItem>
            <Modal
                variant={ModalVariant.small}
                title={t('selectionPage.removeDicomAttributeRule.confirm')}
                isOpen={isRemoveAttributeModalOpen}
                onClose={() => setRemoveAttributeModalOpenState(false)}
                actions={[
                    <Button key="confirmRemoval" variant="danger" onClick={handleRemoveAttributeConfirmation}>{t('selectionPage.removeDicomAttributeRule.short')}</Button>,
                    <Button key="cancel" variant="tertiary" onClick={() => setRemoveAttributeModalOpenState(false)}>{t('common.cancel')}</Button>
                ]}
            >
                {/* eslint-disable-next-line react/no-unescaped-entities */}
                Do you want to remove this DICOM attribute/value condition "{dicomAttributeRule.attribute}: {dicomAttributeRule.value}" from this model selection rule? Note that your changes won't be saved until you click on "Save".
            </Modal>
        </>
    );
}

export default DicomAttributeRuleForm;
